import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import useAgreementPreview from "services/hooks/useAgreementPreview";
import {
  LANDLORD_PAYMENT_MODE_OPTIONS,
  PAY_WEEK_DAY_OPTIONS,
  PAYOUT_FREQUENCY_OPTIONS,
} from "../registerSchema";
import TermsAgreementField from "./TermsAgreementField";

const RegisterLandlordForm = ({
  values,
  touched,
  errors,
  handleBlur,
  handleChange,
  setFieldValue,
}) => {
  const payoutFrequency = values.payout_frequency || "MONTHLY";
  const paymentMode = values.landlord_payment_mode || "MOBILE_MONEY";
  const { previewUrl } = useAgreementPreview({
    values,
    setFieldValue,
  });

  return (
    <Box padding={1} sx={{ gridColumn: "span 4" }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="text.secondary">
            Account details
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            margin="dense"
            label="Business name"
            type="text"
            fullWidth
            variant="outlined"
            name="business_name"
            value={values.business_name}
            onBlur={handleBlur}
            onChange={handleChange}
            error={Boolean(touched.business_name) && Boolean(errors.business_name)}
            helperText={touched.business_name && errors.business_name}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            margin="dense"
            label="First name"
            type="text"
            fullWidth
            variant="outlined"
            name="first_name"
            value={values.first_name}
            onBlur={handleBlur}
            onChange={handleChange}
            error={Boolean(touched.first_name) && Boolean(errors.first_name)}
            helperText={touched.first_name && errors.first_name}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            margin="dense"
            label="Last name"
            type="text"
            fullWidth
            variant="outlined"
            name="last_name"
            value={values.last_name}
            onBlur={handleBlur}
            onChange={handleChange}
            error={Boolean(touched.last_name) && Boolean(errors.last_name)}
            helperText={touched.last_name && errors.last_name}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            name="email"
            value={values.email}
            onBlur={handleBlur}
            onChange={handleChange}
            error={Boolean(touched.email) && Boolean(errors.email)}
            helperText={touched.email && errors.email}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            margin="dense"
            label="Contact (+256)"
            type="tel"
            fullWidth
            variant="outlined"
            name="contact"
            placeholder="+256712345678"
            value={values.contact}
            onBlur={handleBlur}
            onChange={handleChange}
            error={Boolean(touched.contact) && Boolean(errors.contact)}
            helperText={
              (touched.contact && errors.contact) ||
              "Used as login username and phone number"
            }
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }}>
            Payout schedule
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl
            fullWidth
            margin="dense"
            error={Boolean(touched.payout_frequency) && Boolean(errors.payout_frequency)}
          >
            <InputLabel>Payout frequency</InputLabel>
            <Select
              label="Payout frequency"
              name="payout_frequency"
              value={payoutFrequency}
              onBlur={handleBlur}
              onChange={handleChange}
            >
              {PAYOUT_FREQUENCY_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            {touched.payout_frequency && errors.payout_frequency && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {errors.payout_frequency}
              </Typography>
            )}
          </FormControl>
        </Grid>

        {payoutFrequency === "MONTHLY" && (
          <Grid item xs={12} sm={6}>
            <TextField
              margin="dense"
              label="Pay day (day of month)"
              type="text"
              fullWidth
              variant="outlined"
              name="pay_day"
              value={values.pay_day}
              onBlur={handleBlur}
              onChange={handleChange}
              error={Boolean(touched.pay_day) && Boolean(errors.pay_day)}
              helperText={(touched.pay_day && errors.pay_day) || "e.g. 7th"}
            />
          </Grid>
        )}

        {payoutFrequency === "WEEKLY" && (
          <Grid item xs={12} sm={6}>
            <FormControl
              fullWidth
              margin="dense"
              error={Boolean(touched.pay_week_day) && Boolean(errors.pay_week_day)}
            >
              <InputLabel>Pay week day</InputLabel>
              <Select
                label="Pay week day"
                name="pay_week_day"
                value={values.pay_week_day || ""}
                onBlur={handleBlur}
                onChange={handleChange}
              >
                {PAY_WEEK_DAY_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
              {touched.pay_week_day && errors.pay_week_day && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {errors.pay_week_day}
                </Typography>
              )}
            </FormControl>
          </Grid>
        )}

        <Grid item xs={12}>
          <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }}>
            Payout destination
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Stored as the landlord payout profile for matching and disbursements.
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl
            fullWidth
            margin="dense"
            error={
              Boolean(touched.landlord_payment_mode) &&
              Boolean(errors.landlord_payment_mode)
            }
          >
            <InputLabel>Payment mode</InputLabel>
            <Select
              label="Payment mode"
              name="landlord_payment_mode"
              value={paymentMode}
              onBlur={handleBlur}
              onChange={handleChange}
            >
              {LANDLORD_PAYMENT_MODE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option.replace(/_/g, " ")}
                </MenuItem>
              ))}
            </Select>
            {touched.landlord_payment_mode && errors.landlord_payment_mode && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {errors.landlord_payment_mode}
              </Typography>
            )}
          </FormControl>
        </Grid>

        {paymentMode === "MOBILE_MONEY" && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="Mobile money number"
                type="text"
                fullWidth
                variant="outlined"
                name="landlord_payment_account"
                value={values.landlord_payment_account}
                onBlur={handleBlur}
                onChange={handleChange}
                error={
                  Boolean(touched.landlord_payment_account) &&
                  Boolean(errors.landlord_payment_account)
                }
                helperText={
                  (touched.landlord_payment_account && errors.landlord_payment_account) ||
                  "Optional — defaults to contact (+256) if empty"
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                label="Name on mobile money wallet"
                type="text"
                fullWidth
                variant="outlined"
                name="landlord_mobile_registered_name"
                value={values.landlord_mobile_registered_name}
                onBlur={handleBlur}
                onChange={handleChange}
                error={
                  Boolean(touched.landlord_mobile_registered_name) &&
                  Boolean(errors.landlord_mobile_registered_name)
                }
                helperText={
                  touched.landlord_mobile_registered_name &&
                  errors.landlord_mobile_registered_name
                }
              />
            </Grid>
          </>
        )}

        {paymentMode === "BANK" && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="Bank name"
                type="text"
                fullWidth
                variant="outlined"
                name="landlord_bank_name"
                value={values.landlord_bank_name}
                onBlur={handleBlur}
                onChange={handleChange}
                error={
                  Boolean(touched.landlord_bank_name) && Boolean(errors.landlord_bank_name)
                }
                helperText={touched.landlord_bank_name && errors.landlord_bank_name}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="Account number"
                type="text"
                fullWidth
                variant="outlined"
                name="landlord_bank_account_number"
                value={values.landlord_bank_account_number}
                onBlur={handleBlur}
                onChange={handleChange}
                error={
                  Boolean(touched.landlord_bank_account_number) &&
                  Boolean(errors.landlord_bank_account_number)
                }
                helperText={
                  touched.landlord_bank_account_number &&
                  errors.landlord_bank_account_number
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="Account name"
                type="text"
                fullWidth
                variant="outlined"
                name="landlord_bank_account_name"
                value={values.landlord_bank_account_name}
                onBlur={handleBlur}
                onChange={handleChange}
                error={
                  Boolean(touched.landlord_bank_account_name) &&
                  Boolean(errors.landlord_bank_account_name)
                }
                helperText={
                  touched.landlord_bank_account_name && errors.landlord_bank_account_name
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="Branch"
                type="text"
                fullWidth
                variant="outlined"
                name="landlord_bank_branch"
                value={values.landlord_bank_branch}
                onBlur={handleBlur}
                onChange={handleChange}
                error={
                  Boolean(touched.landlord_bank_branch) && Boolean(errors.landlord_bank_branch)
                }
                helperText={touched.landlord_bank_branch && errors.landlord_bank_branch}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="Sort code (optional)"
                type="text"
                fullWidth
                variant="outlined"
                name="landlord_bank_sort_code"
                value={values.landlord_bank_sort_code}
                onBlur={handleBlur}
                onChange={handleChange}
                error={
                  Boolean(touched.landlord_bank_sort_code) &&
                  Boolean(errors.landlord_bank_sort_code)
                }
                helperText={
                  touched.landlord_bank_sort_code && errors.landlord_bank_sort_code
                }
              />
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <TermsAgreementField
            values={values}
            touched={touched}
            errors={errors}
            handleBlur={handleBlur}
            setFieldValue={setFieldValue}
            previewUrl={previewUrl}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default RegisterLandlordForm;
